import { axios } from "@/client/libs/axios";
import { getPersistentFingerprint } from "@/client/utils/fingerprint";

export type GuestSessionResponse = {
  success: boolean;
  guestId: string;
  tokenBalance: number;
  tokensUsed: number;
  expiresAt: string;
  requireSignup?: boolean;
  error?: string;
};

export type ResumeDataResponse = {
  success: boolean;
  resumeData: any;
  message: string;
};

export type SaveResumeDataRequest = {
  resumeData: any;
};

export type SaveResumeDataResponse = {
  success: boolean;
  message: string;
};

export type ImportResumeResponse = {
  id: string;
  userId: string;
  title: string;
  slug: string;
  visibility: string;
  locked: boolean;
  data: any;
  createdAt: string;
  updatedAt: string;
};

/**
 * Guest Session Service for handling guest user sessions and resume data
 */
export const GuestSessionService = {
  /**
   * Initialize or get guest session
   * Includes browser fingerprint for rate limiting protection
   */
  async initializeSession(): Promise<GuestSessionResponse> {
    const fingerprint = getPersistentFingerprint();
    const response = await axios.post<GuestSessionResponse>("/guest/session", {
      fingerprint,
    });
    return response.data;
  },

  /**
   * Get guest token balance and usage
   * Auto-creates session if none exists (with rate limiting)
   */
  async getTokens(): Promise<{
    tokenBalance: number;
    tokensUsed: number;
    requireSignup?: boolean;
    error?: string;
  }> {
    const response = await axios.get<{
      tokenBalance: number;
      tokensUsed: number;
      requireSignup?: boolean;
      error?: string;
    }>("/guest/tokens");
    return response.data;
  },

  /**
   * Save AI-generated resume data for guest session
   */
  async saveResumeData(resumeData: any): Promise<SaveResumeDataResponse> {
    const { useBuilderStore } = await import("@/client/stores/builder");
    const setIsSaving = useBuilderStore.getState().setIsSaving;
    setIsSaving(true);
    
    try {

      const response = await axios.post<SaveResumeDataResponse>("/guest/resume-data", {
        resumeData,
      });

      return response.data;
    } finally {
      setIsSaving(false);
    }
  },

  /**
   * Get AI-generated resume data for guest session
   */
  async getResumeData(): Promise<ResumeDataResponse> {

    try {
      const response = await axios.get<ResumeDataResponse>("/guest/resume-data");

      return response.data;
    } catch (error: any) {
      console.error("❌ Failed to get resume data:", error);
      // Return a default response instead of throwing
      return {
        success: false,
        resumeData: null,
        message: error?.message || "Failed to load resume data",
      };
    }
  },

  /**
   * Clear AI-generated resume data for guest session
   */
  async clearResumeData(): Promise<{ success: boolean; message: string }> {
    const response = await axios.delete<{ success: boolean; message: string }>(
      "/guest/resume-data",
    );
    return response.data;
  },

  /**
   * Import resume for guest session (same flow as authenticated users)
   */
  async importResume(importPayload: any): Promise<ImportResumeResponse> {

    const response = await axios.post<ImportResumeResponse>("/guest/resume/import", importPayload);

    return response.data;
  },

  /**
   * Helper: Check if user has a guest session
   * NOTE: Since guestId is an HTTP-only cookie, we can't read it from JavaScript.
   * Instead, we'll make an API call to check session status.
   */
  async hasGuestSession(): Promise<boolean> {
    try {
      const response = await axios.get("/guest/session/check");
      return response.data.valid === true;
    } catch (error) {
      console.warn("Failed to check guest session status:", error);
      return false;
    }
  },

  /**
   * Helper: Get guest session ID from cookie
   * NOTE: HTTP-only cookies cannot be read by JavaScript.
   * This method is kept for backward compatibility but will return null.
   */
  getGuestSessionId(): string | null {
    console.warn("getGuestSessionId: HTTP-only cookies cannot be read by JavaScript");
    return null; // HTTP-only cookies are not accessible to JavaScript
  },
};
